import React, { useState, useEffect } from 'react';
import { Briefcase, MapPin, Clock, Users, Mail, ArrowRight, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getCareers } from '../services/api';
import { motion } from 'framer-motion';

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getCareers();
        setJobs(res.data || []);
      } catch {}
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white" data-testid="careers-page">
      {/* Header */}
      <section className="bg-[#2E3A6F] text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="text-4xl sm:text-5xl font-bold">Careers at Namoh Toolings</h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Join our team of passionate engineers and tooling experts
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          {loading ? (
            <div className="space-y-4">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-8 h-32" /></Card>)}</div>
          ) : jobs.length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-12 text-center space-y-4">
                <Briefcase className="mx-auto text-gray-300" size={48} />
                <h3 className="text-xl font-bold text-gray-900">No Current Openings</h3>
                <p className="text-gray-600">We don't have any openings right now, but we're always looking for talented people.</p>
                <p className="text-sm text-gray-500">Send your resume to <a href="mailto:hr@namohtoolings.in" className="text-[#2E3A6F] font-medium hover:underline">hr@namohtoolings.in</a></p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <p className="text-gray-600">{jobs.length} open position{jobs.length > 1 ? 's' : ''}</p>
              {jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card className="border-0 hover:shadow-xl transition-all duration-300 overflow-hidden" data-testid={`career-card-${job.id}`}>
                    <CardContent className="p-6 space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="space-y-2">
                          <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
                          <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                            {job.department && <span className="flex items-center gap-1 bg-gray-50 px-3 py-1 rounded-full"><Users size={14} /> {job.department}</span>}
                            {job.location && <span className="flex items-center gap-1 bg-gray-50 px-3 py-1 rounded-full"><MapPin size={14} /> {job.location}</span>}
                            {job.type && <span className="flex items-center gap-1 bg-gray-50 px-3 py-1 rounded-full"><Clock size={14} /> {job.type}</span>}
                            {job.experience && <span className="flex items-center gap-1 bg-gray-50 px-3 py-1 rounded-full"><Briefcase size={14} /> {job.experience}</span>}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          className="border-[#2E3A6F] text-[#2E3A6F] hover:bg-[#2E3A6F] hover:text-white whitespace-nowrap"
                          onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                        >
                          {expandedJob === job.id ? 'Hide Details' : 'View Details'} <ArrowRight size={14} className="ml-1" />
                        </Button>
                      </div>

                      {expandedJob === job.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="pt-4 border-t space-y-4"
                        >
                          {job.description && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Job Description</h4>
                              <p className="text-gray-700 text-sm whitespace-pre-line leading-relaxed">{job.description}</p>
                            </div>
                          )}
                          {job.requirements && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Requirements</h4>
                              <div className="text-gray-700 text-sm space-y-1">
                                {job.requirements.split('\n').filter(Boolean).map((req, i) => (
                                  <p key={i} className="flex items-start gap-2">
                                    <span className="text-[#2E3A6F] mt-1 flex-shrink-0">-</span>
                                    {req}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="bg-[#2E3A6F]/5 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Mail size={16} className="text-[#2E3A6F]" />
                              <span className="text-sm text-gray-700">Send your resume to:</span>
                            </div>
                            <a
                              href={`mailto:${job.email}?subject=Application for ${job.title}`}
                              className="inline-flex items-center gap-2 bg-[#2E3A6F] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1f2747] transition-colors"
                              data-testid={`apply-btn-${job.id}`}
                            >
                              <Send size={14} /> Apply via Email ({job.email})
                            </a>
                          </div>
                        </motion.div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Careers;
